import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SessionGuard } from "./components/SessionGuard";
import { RoleRoute, HomeRedirect } from "./components/RoleRoute";

// Route-level code splitting: only Landing/SignIn and the shells ship in the initial chunk.
const LegalIndex = lazy(() => import("./pages/LegalIndex"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const PlatformAccess = lazy(() => import("./pages/PlatformAccess"));
const NetworkHome = lazy(() => import("./network/NetworkHome"));
const PersonProfile = lazy(() => import("./network/NetworkHome").then((m) => ({ default: m.PersonProfile })));
const OrgProfile = lazy(() => import("./network/NetworkHome").then((m) => ({ default: m.OrgProfile })));
const Register = lazy(() => import("./pages/Register"));
const PartnerRegister = lazy(() => import("./pages/PartnerRegister"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Activate = lazy(() => import("./pages/Activate"));
const PortalLayout = lazy(() => import("./portal/PortalLayout"));
const PortalHome = lazy(() => import("./portal/PortalHome"));
const PortalAppointments = lazy(() => import("./portal/PortalAppointments"));
const PortalPrescriptions = lazy(() => import("./portal/PortalPrescriptions"));
const PortalReferrals = lazy(() => import("./portal/PortalReferrals"));
const PortalMessages = lazy(() => import("./portal/PortalMessages"));
const PortalMyRequests = lazy(() => import("./portal/PortalMyRequests"));
const PortalDocuments = lazy(() => import("./portal/PortalDocuments"));
const PortalInvoices = lazy(() => import("./portal/PortalInvoices"));
const PortalAccount = lazy(() => import("./portal/PortalAccount"));
const ManagedPeople = lazy(() => import("./portal/ManagedPeople"));
const PortalHealthCard = lazy(() => import("./portal/PortalHealthCard"));
const InternalLayout = lazy(() => import("./internal/InternalLayout"));
const Hub = lazy(() => import("./internal/Hub"));
const RxQueue = lazy(() => import("./internal/RxQueue"));
const PharmacyIntake = lazy(() => import("./internal/PharmacyIntake"));
const SendPrescription = lazy(() => import("./internal/SendPrescription"));
const Professionals = lazy(() => import("./internal/Professionals"));
const AppointmentQueue = lazy(() => import("./internal/AppointmentQueue"));
const Calendar = lazy(() => import("./internal/Calendar"));
const ExamsResults = lazy(() => import("./internal/ExamsResults"));
const MessageQueue = lazy(() => import("./internal/MessageQueue"));
const TaskQueue = lazy(() => import("./internal/TaskQueue"));
const WorkHub = lazy(() => import("./internal/WorkHub"));
const Referrals = lazy(() => import("./internal/Referrals"));
const Verifications = lazy(() => import("./internal/Verifications"));
const Applications = lazy(() => import("./internal/Applications"));
const Settings = lazy(() => import("./internal/Settings"));
const ClinicProfile = lazy(() => import("./internal/ClinicProfile"));
const NotificationsPage = lazy(() => import("./portal/NotificationCenter"));
const DeveloperPortal = lazy(() => import("./developer/DeveloperPortal"));
const AuditCompliance = lazy(() => import("./internal/AuditCompliance"));
const Patients = lazy(() => import("./internal/Patients"));
const EncounterPage = lazy(() => import("./encounter/EncounterPage"));
const Organizations = lazy(() => import("./internal/Organizations"));
const Providers = lazy(() => import("./internal/Providers"));
const PrivateRequests = lazy(() => import("./internal/PrivateRequests"));
const Billing = lazy(() => import("./internal/Billing"));
const RegistrationQR = lazy(() => import("./internal/RegistrationQR"));
const PortalAccessRequests = lazy(() => import("./internal/PortalAccessRequests"));
const HealthCardUpdates = lazy(() => import("./internal/HealthCardUpdates"));
const PartnerLayout = lazy(() => import("./partner/PartnerLayout"));
const PartnerProfile = lazy(() => import("./partner/PartnerProfile"));
const PharmacyLayout = lazy(() => import("./pharmacy/PharmacyLayout"));
const PharmacyHub = lazy(() => import("./pharmacy/PharmacyHub"));
const PharmacyRx = lazy(() => import("./pharmacy/PharmacyRx"));
const PharmacyMessages = lazy(() => import("./pharmacy/PharmacyMessages"));
const PharmacyIncoming = lazy(() => import("./pharmacy/PharmacyIncoming"));
const PharmacyAccount = lazy(() => import("./pharmacy/PharmacyAccount"));
const PortalPreview = lazy(() => import("./internal/PortalPreview"));

import Landing from "./pages/Landing";
import SignIn from "./pages/SignIn";



// Physicians land on Patients; every other internal role keeps the Work Hub.
function InternalIndex() {
    const { user } = useAuth();
    const isPhysician = user?.role === "physician" || user?.base_role === "physician";
    return isPhysician ? <Navigate to="/internal/patients" replace /> : <Hub />;
}
import { SystemStatusProvider, ManagedNotice } from "./components/SystemStatus";
import { OutageBoundary } from "./components/OutageBoundary";
import { useLocation } from "react-router-dom";

// Public VIen pages (login, sign-in, registration, activation…) show the managed notice; authenticated shells get it via RoleRoute.
const PUBLIC_NOTICE_PATHS = ["/login", "/signin", "/register", "/platform-access", "/partners/register", "/change-password", "/forgot-password", "/activate"];
function PublicNotice() {
    const { pathname } = useLocation();
    return PUBLIC_NOTICE_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/")) ? <ManagedNotice /> : null;
}



function RouteLoader() {
    return <div className="min-h-[40vh] flex items-center justify-center" aria-busy="true" data-testid="route-loader"><div className="text-visita-green font-plex font-semibold animate-pulse">Loading VIen…</div></div>;
}

function App() {
    return (
        <AuthProvider>
            <SystemStatusProvider>
            <Toaster position="top-center" richColors />
            <BrowserRouter>
                <OutageBoundary>
                <SessionGuard />
                <PublicNotice />
                <Suspense fallback={<RouteLoader />}>
                <Routes>
                    <Route path="/" element={<HomeRedirect />} />
                    <Route path="/login" element={<Landing />} />
                    <Route path="/legal" element={<LegalIndex />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/account-deletion" element={<Navigate to="/privacy-policy#deletion-en" replace />} />
                    <Route path="/platform-access" element={<PlatformAccess />} />
                    <Route path="/signin" element={<SignIn />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/register/:practiceSlug" element={<Register />} />
                    <Route path="/partners/register" element={<PartnerRegister />} />
                    <Route path="/change-password" element={<ChangePassword />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/activate" element={<Activate />} />

                    <Route
                        path="/portal"
                        element={
                            <RoleRoute roles={["patient", "caregiver"]}>
                                <PortalLayout />
                            </RoleRoute>
                        }
                    >
                        <Route index element={<PortalHome />} />
                        <Route path="people" element={<ManagedPeople />} />
                        <Route path="manage/:patientId">
                            <Route index element={<PortalHome />} />
                            <Route path="appointments" element={<PortalAppointments />} />
                            <Route path="prescriptions" element={<PortalPrescriptions />} />
                            <Route path="documents" element={<PortalDocuments />} />
                            <Route path="requests" element={<PortalMyRequests />} />
                            <Route path="health-card" element={<PortalHealthCard />} />
                        </Route>
                        <Route path="appointments" element={<PortalAppointments />} />
                        <Route path="prescriptions" element={<PortalPrescriptions />} />
                        <Route path="referrals" element={<PortalReferrals />} />
                        <Route path="messages" element={<PortalMessages />} />
                        <Route path="imaging" element={<Navigate to="/portal/appointments" replace />} />
                        <Route path="bloodwork" element={<Navigate to="/portal/appointments" replace />} />
                        <Route path="requests" element={<PortalMyRequests />} />
                        <Route path="documents" element={<PortalDocuments />} />
                        <Route path="invoices" element={<PortalInvoices />} />
                        <Route path="notifications" element={<NotificationsPage />} />
                        <Route path="account" element={<PortalAccount />} />
                    </Route>

                    <Route path="/developer" element={<RoleRoute roles={["developer_ops", "operations_operator"]}><DeveloperPortal /></RoleRoute>} />
                    <Route path="/network" element={<RoleRoute roles={["network", "physician", "admin", "staff"]}><NetworkHome /></RoleRoute>} />
                    <Route path="/network/people/:id" element={<RoleRoute roles={["network", "physician", "admin", "staff"]}><PersonProfile /></RoleRoute>} />
                    <Route path="/network/orgs/:id" element={<RoleRoute roles={["network", "physician", "admin", "staff"]}><OrgProfile /></RoleRoute>} />
                    <Route path="/internal/patients/:patientId/portal-preview" element={<RoleRoute roles={["admin", "physician", "staff"]}><PortalPreview /></RoleRoute>} />
                    <Route
                        path="/internal/encounter"
                        element={
                            <RoleRoute roles={["physician"]} requireEncounter>
                                <EncounterPage />
                            </RoleRoute>
                        }
                    />
                    <Route
                        path="/internal/encounter/:visitId"
                        element={
                            <RoleRoute roles={["physician"]} requireEncounter>
                                <EncounterPage />
                            </RoleRoute>
                        }
                    />

                    <Route
                        path="/internal"
                        element={
                            <RoleRoute roles={["staff", "physician", "admin"]}>
                                <InternalLayout />
                            </RoleRoute>
                        }
                    >
                        <Route index element={<InternalIndex />} />
                        <Route path="patients" element={<Patients />} />
                        <Route path="rx" element={<RxQueue />} />
                        <Route path="pharmacy-intake" element={<PharmacyIntake />} />
                        <Route path="send-rx" element={<SendPrescription />} />
                        <Route path="appointments" element={<AppointmentQueue />} />
                        <Route path="private-requests" element={<PrivateRequests />} />
                        <Route path="billing" element={<Billing />} />
                        <Route path="calendar" element={<Calendar />} />
                        <Route path="exams" element={<ExamsResults />} />
                        <Route path="imaging" element={<Navigate to="/internal/exams" replace />} />
                        <Route path="bloodwork" element={<Navigate to="/internal/exams" replace />} />
                        <Route path="messages" element={<MessageQueue />} />
                        <Route path="work" element={<WorkHub />} />
                        <Route path="tasks" element={<TaskQueue />} /> {/* legacy intercom — kept for rollback during Preview */}
                        <Route path="referrals" element={<Referrals />} />
                        <Route path="verifications" element={<Verifications />} />
                        <Route path="applications" element={<Applications />} />
                        <Route path="registration-qr" element={<RegistrationQR />} />
                        <Route path="portal-access-requests" element={<PortalAccessRequests />} />
                        <Route path="health-card-updates" element={<HealthCardUpdates />} />
                        <Route path="organizations" element={<Organizations />} />
                        <Route path="providers" element={<Providers />} />
                        <Route path="professionals" element={<Professionals />} />
                        <Route path="settings" element={<Settings />} />
                        <Route path="clinic" element={<ClinicProfile />} />
                        <Route path="settings/audit" element={<AuditCompliance />} />
                    </Route>

                    <Route
                        path="/partner"
                        element={
                            <RoleRoute roles={["partner"]}>
                                <PartnerLayout />
                            </RoleRoute>
                        }
                    >
                        <Route index element={<PartnerProfile />} />
                    </Route>

                    <Route
                        path="/pharmacy"
                        element={
                            <RoleRoute roles={["pharmacy"]}>
                                <PharmacyLayout />
                            </RoleRoute>
                        }
                    >
                        <Route index element={<PharmacyHub />} />
                        <Route path="rx" element={<PharmacyRx />} />
                        <Route path="incoming" element={<PharmacyIncoming />} />
                        <Route path="messages" element={<PharmacyMessages />} />
                        <Route path="account" element={<PharmacyAccount />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
                </OutageBoundary>
            </BrowserRouter>
            </SystemStatusProvider>
        </AuthProvider>
    );
}

export default App;
